export interface JobRole {
  id: string;
  label: string;
  category: string;
  aliases: string[];
  description: string;
  commonQuestionTypes: string[];
}

export interface JobRoleCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  roles: JobRole[];
}

export const JOB_ROLE_CATEGORIES: JobRoleCategory[] = [
  {
    id: 'development',
    name: 'Software Development',
    icon: '💻',
    description: 'Software engineering and development roles',
    roles: [
      {
        id: 'frontend-developer',
        label: 'Frontend Developer',
        category: 'development',
        aliases: ['frontend', 'ui developer', 'front end'],
        description: 'Builds user interfaces and client-side applications',
        commonQuestionTypes: ['technical', 'coding', 'behavioral']
      },
      {
        id: 'backend-developer',
        label: 'Backend Developer',
        category: 'development',
        aliases: ['backend', 'server-side developer', 'back end'],
        description: 'Develops server-side logic and APIs',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'fullstack-developer',
        label: 'Full-Stack Developer',
        category: 'development',
        aliases: ['fullstack', 'full stack', 'full-stack engineer'],
        description: 'Works on both frontend and backend',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'mobile-developer',
        label: 'Mobile Developer',
        category: 'development',
        aliases: ['mobile', 'ios developer', 'android developer', 'app developer'],
        description: 'Creates mobile applications for iOS/Android',
        commonQuestionTypes: ['technical', 'coding', 'behavioral']
      },
      {
        id: 'game-developer',
        label: 'Game Developer',
        category: 'development',
        aliases: ['game dev', 'game programmer', 'unity developer'],
        description: 'Develops video games and interactive experiences',
        commonQuestionTypes: ['technical', 'coding', 'behavioral']
      },
      {
        id: 'embedded-systems',
        label: 'Embedded Systems Engineer',
        category: 'development',
        aliases: ['embedded', 'firmware engineer', 'iot developer'],
        description: 'Works on embedded systems and firmware',
        commonQuestionTypes: ['technical', 'coding', 'situational']
      }
    ]
  },
  {
    id: 'data-analytics',
    name: 'Data & Analytics',
    icon: '📊',
    description: 'Data science, analytics, and machine learning roles',
    roles: [
      {
        id: 'data-scientist',
        label: 'Data Scientist',
        category: 'data-analytics',
        aliases: ['data science', 'ml scientist', 'research scientist'],
        description: 'Analyzes data and builds predictive models',
        commonQuestionTypes: ['technical', 'coding', 'situational']
      },
      {
        id: 'data-engineer',
        label: 'Data Engineer',
        category: 'data-analytics',
        aliases: ['data', 'big data engineer', 'etl developer'],
        description: 'Builds and maintains data pipelines',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'data-analyst',
        label: 'Data Analyst',
        category: 'data-analytics',
        aliases: ['analyst', 'business intelligence analyst'],
        description: 'Analyzes data to provide business insights',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'ml-engineer',
        label: 'ML Engineer',
        category: 'data-analytics',
        aliases: ['ml', 'machine learning engineer', 'ai engineer'],
        description: 'Develops and deploys machine learning models',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'ai-developer',
        label: 'AI Developer',
        category: 'data-analytics',
        aliases: ['artificial intelligence', 'ai specialist', 'deep learning engineer'],
        description: 'Builds AI systems and neural networks',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'ai-analyst',
        label: 'Artificial Intelligence Analyst',
        category: 'data-analytics',
        aliases: ['ai analyst', 'ai researcher'],
        description: 'Analyzes and optimizes AI systems',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      }
    ]
  },
  {
    id: 'infrastructure-ops',
    name: 'Infrastructure & Operations',
    icon: '⚙️',
    description: 'DevOps, cloud, and infrastructure roles',
    roles: [
      {
        id: 'devops-engineer',
        label: 'DevOps Engineer',
        category: 'infrastructure-ops',
        aliases: ['devops', 'site reliability engineer', 'sre'],
        description: 'Manages deployment pipelines and infrastructure',
        commonQuestionTypes: ['technical', 'system-design', 'situational']
      },
      {
        id: 'cloud-engineer',
        label: 'Cloud Engineer',
        category: 'infrastructure-ops',
        aliases: ['cloud', 'aws engineer', 'azure engineer', 'gcp engineer'],
        description: 'Designs and manages cloud infrastructure',
        commonQuestionTypes: ['technical', 'system-design', 'situational']
      },
      {
        id: 'systems-administrator',
        label: 'Systems Administrator',
        category: 'infrastructure-ops',
        aliases: ['sysadmin', 'systems admin', 'it admin'],
        description: 'Maintains and configures IT systems',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'network-engineer',
        label: 'Network Engineer',
        category: 'infrastructure-ops',
        aliases: ['network', 'network admin', 'networking specialist'],
        description: 'Designs and manages network infrastructure',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'dba',
        label: 'Database Administrator (DBA)',
        category: 'infrastructure-ops',
        aliases: ['database admin', 'db admin', 'database engineer'],
        description: 'Manages and optimizes databases',
        commonQuestionTypes: ['technical', 'system-design', 'situational']
      }
    ]
  },
  {
    id: 'security',
    name: 'Security & Compliance',
    icon: '🔒',
    description: 'Cybersecurity and information security roles',
    roles: [
      {
        id: 'security-engineer',
        label: 'Security Engineer',
        category: 'security',
        aliases: ['security', 'infosec engineer', 'cybersecurity engineer'],
        description: 'Protects systems from security threats',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'security-analyst',
        label: 'Security Analyst',
        category: 'security',
        aliases: ['cybersecurity analyst', 'infosec analyst'],
        description: 'Monitors and responds to security incidents',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'penetration-tester',
        label: 'Penetration Tester',
        category: 'security',
        aliases: ['pentester', 'ethical hacker', 'security tester'],
        description: 'Tests systems for security vulnerabilities',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'compliance-officer',
        label: 'Compliance Officer',
        category: 'security',
        aliases: ['compliance', 'security compliance', 'risk officer'],
        description: 'Ensures regulatory and security compliance',
        commonQuestionTypes: ['situational', 'behavioral', 'technical']
      }
    ]
  },
  {
    id: 'quality',
    name: 'Quality Assurance',
    icon: '✅',
    description: 'Testing and quality assurance roles',
    roles: [
      {
        id: 'qa-engineer',
        label: 'QA Engineer',
        category: 'quality',
        aliases: ['qa', 'quality assurance', 'test engineer'],
        description: 'Tests software for bugs and issues',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'quality-analyst',
        label: 'Quality Analyst',
        category: 'quality',
        aliases: ['qa analyst', 'quality assurance analyst'],
        description: 'Analyzes and improves software quality',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'automation-engineer',
        label: 'Test Automation Engineer',
        category: 'quality',
        aliases: ['automation', 'sdet', 'qa automation'],
        description: 'Develops automated testing frameworks',
        commonQuestionTypes: ['technical', 'coding', 'situational']
      },
      {
        id: 'performance-tester',
        label: 'Performance Tester',
        category: 'quality',
        aliases: ['performance engineer', 'load tester'],
        description: 'Tests application performance and scalability',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      }
    ]
  },
  {
    id: 'project-program',
    name: 'Project & Program Management',
    icon: '📋',
    description: 'Project management and delivery roles',
    roles: [
      {
        id: 'project-manager',
        label: 'Project Manager',
        category: 'project-program',
        aliases: ['pm', 'project lead', 'delivery manager'],
        description: 'Manages project execution and delivery',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'program-manager',
        label: 'Program Manager',
        category: 'project-program',
        aliases: ['program lead', 'portfolio manager'],
        description: 'Oversees multiple related projects',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'scrum-master',
        label: 'Scrum Master',
        category: 'project-program',
        aliases: ['agile coach', 'scrum facilitator'],
        description: 'Facilitates agile development processes',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'release-manager',
        label: 'Release Manager',
        category: 'project-program',
        aliases: ['release engineer', 'deployment manager'],
        description: 'Manages software releases and deployments',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      }
    ]
  },
  {
    id: 'product',
    name: 'Product Management',
    icon: '🎯',
    description: 'Product strategy and management roles',
    roles: [
      {
        id: 'product-manager',
        label: 'Product Manager',
        category: 'product',
        aliases: ['pm', 'product lead', 'product owner'],
        description: 'Defines product strategy and roadmap',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'technical-product-manager',
        label: 'Technical Product Manager',
        category: 'product',
        aliases: ['technical pm', 'tpm'],
        description: 'Product management with technical focus',
        commonQuestionTypes: ['behavioral', 'technical', 'situational']
      },
      {
        id: 'product-owner',
        label: 'Product Owner',
        category: 'product',
        aliases: ['po', 'agile product owner'],
        description: 'Manages product backlog and priorities',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      }
    ]
  },
  {
    id: 'business-analysis',
    name: 'Business Analysis',
    icon: '📈',
    description: 'Business analysis and requirements roles',
    roles: [
      {
        id: 'business-analyst',
        label: 'Business Analyst',
        category: 'business-analysis',
        aliases: ['ba', 'business systems analyst'],
        description: 'Analyzes business needs and requirements',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'systems-analyst',
        label: 'Systems Analyst',
        category: 'business-analysis',
        aliases: ['it analyst', 'technical analyst'],
        description: 'Analyzes and designs IT systems',
        commonQuestionTypes: ['technical', 'situational', 'behavioral']
      },
      {
        id: 'requirements-engineer',
        label: 'Requirements Engineer',
        category: 'business-analysis',
        aliases: ['requirements analyst', 'business requirements analyst'],
        description: 'Gathers and documents system requirements',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      }
    ]
  },
  {
    id: 'design-ux',
    name: 'Design & UX',
    icon: '🎨',
    description: 'User experience and design roles',
    roles: [
      {
        id: 'ux-designer',
        label: 'UX Designer',
        category: 'design-ux',
        aliases: ['user experience designer', 'ux/ui designer'],
        description: 'Designs user experiences and interfaces',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'ui-designer',
        label: 'UI Designer',
        category: 'design-ux',
        aliases: ['user interface designer', 'visual designer'],
        description: 'Creates visual interface designs',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'ux-researcher',
        label: 'UX Researcher',
        category: 'design-ux',
        aliases: ['user researcher', 'design researcher'],
        description: 'Conducts user research and usability testing',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'product-designer',
        label: 'Product Designer',
        category: 'design-ux',
        aliases: ['design lead', 'interaction designer'],
        description: 'End-to-end product design',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      }
    ]
  },
  {
    id: 'engineering-leadership',
    name: 'Engineering Leadership',
    icon: '👥',
    description: 'Technical leadership and management roles',
    roles: [
      {
        id: 'engineering-manager',
        label: 'Engineering Manager',
        category: 'engineering-leadership',
        aliases: ['manager', 'em', 'dev manager', 'team lead'],
        description: 'Manages engineering teams and delivery',
        commonQuestionTypes: ['behavioral', 'situational', 'technical']
      },
      {
        id: 'tech-lead',
        label: 'Tech Lead',
        category: 'engineering-leadership',
        aliases: ['technical lead', 'lead developer', 'lead engineer'],
        description: 'Provides technical leadership to team',
        commonQuestionTypes: ['technical', 'behavioral', 'system-design']
      },
      {
        id: 'architect',
        label: 'Software Architect',
        category: 'engineering-leadership',
        aliases: ['solutions architect', 'enterprise architect', 'technical architect'],
        description: 'Designs high-level system architecture',
        commonQuestionTypes: ['system-design', 'technical', 'behavioral']
      },
      {
        id: 'staff-engineer',
        label: 'Staff Engineer',
        category: 'engineering-leadership',
        aliases: ['staff', 'senior staff engineer'],
        description: 'Senior individual contributor role',
        commonQuestionTypes: ['technical', 'system-design', 'behavioral']
      },
      {
        id: 'principal-engineer',
        label: 'Principal Engineer',
        category: 'engineering-leadership',
        aliases: ['principal', 'distinguished engineer'],
        description: 'Highest level individual contributor',
        commonQuestionTypes: ['system-design', 'technical', 'behavioral']
      },
      {
        id: 'cto',
        label: 'Chief Technology Officer (CTO)',
        category: 'engineering-leadership',
        aliases: ['cto', 'vp engineering', 'head of engineering'],
        description: 'Leads overall technology strategy',
        commonQuestionTypes: ['behavioral', 'situational', 'system-design']
      }
    ]
  },
  {
    id: 'specialized',
    name: 'Specialized Roles',
    icon: '🔬',
    description: 'Specialized and niche technical roles',
    roles: [
      {
        id: 'blockchain-developer',
        label: 'Blockchain Developer',
        category: 'specialized',
        aliases: ['blockchain', 'web3 developer', 'smart contract developer'],
        description: 'Develops blockchain and cryptocurrency applications',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'ar-vr-developer',
        label: 'AR/VR Developer',
        category: 'specialized',
        aliases: ['augmented reality', 'virtual reality', 'xr developer'],
        description: 'Creates AR/VR experiences',
        commonQuestionTypes: ['technical', 'coding', 'behavioral']
      },
      {
        id: 'graphics-engineer',
        label: 'Graphics Engineer',
        category: 'specialized',
        aliases: ['graphics programmer', 'rendering engineer'],
        description: 'Works on graphics and rendering systems',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'computer-vision',
        label: 'Computer Vision Engineer',
        category: 'specialized',
        aliases: ['cv engineer', 'image processing engineer'],
        description: 'Develops computer vision systems',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'nlp-engineer',
        label: 'NLP Engineer',
        category: 'specialized',
        aliases: ['natural language processing', 'nlp specialist'],
        description: 'Works on natural language processing',
        commonQuestionTypes: ['technical', 'coding', 'system-design']
      },
      {
        id: 'robotics-engineer',
        label: 'Robotics Engineer',
        category: 'specialized',
        aliases: ['robotics', 'automation engineer'],
        description: 'Develops robotic systems',
        commonQuestionTypes: ['technical', 'coding', 'situational']
      }
    ]
  }
];

// Utility functions
export const getAllJobRoles = (): JobRole[] => {
  return JOB_ROLE_CATEGORIES.flatMap(category => category.roles);
};

export const getJobRoleById = (id: string): JobRole | undefined => {
  return getAllJobRoles().find(role => role.id === id);
};

export const getJobRolesByCategory = (categoryId: string): JobRole[] => {
  const category = JOB_ROLE_CATEGORIES.find(cat => cat.id === categoryId);
  return category?.roles || [];
};

export const searchJobRoles = (query: string): JobRole[] => {
  const lowerQuery = query.toLowerCase();
  return getAllJobRoles().filter(role => 
    role.label.toLowerCase().includes(lowerQuery) ||
    role.aliases.some(alias => alias.toLowerCase().includes(lowerQuery)) ||
    role.description.toLowerCase().includes(lowerQuery)
  );
};

export const getRecommendedQuestionTypes = (roleId: string): string[] => {
  const role = getJobRoleById(roleId);
  return role?.commonQuestionTypes || [];
};

// Legacy role mapping for backward compatibility
export const LEGACY_ROLE_MAPPING: Record<string, string> = {
  'frontend': 'frontend-developer',
  'backend': 'backend-developer',
  'fullstack': 'fullstack-developer',
  'devops': 'devops-engineer',
  'data': 'data-engineer',
  'ml': 'ml-engineer',
  'mobile': 'mobile-developer',
  'qa': 'qa-engineer',
  'security': 'security-engineer',
  'manager': 'engineering-manager'
};

export const mapLegacyRole = (legacyRole: string): string => {
  return LEGACY_ROLE_MAPPING[legacyRole] || legacyRole;
};
